import { useState, useEffect } from 'react';
import { generateAiResponse, sendAiResponse, getOpenRouterModels, improvePromptWithAi, saveLearnedInstructions, applyMasterPromptSuggestion } from '../../services/apiTutor';

export default function AiResponseModal({ isOpen, onClose, conversationId, conversation, onSuccess }) {
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [responseText, setResponseText] = useState('');
    const [reasoning, setReasoning] = useState('');
    const [warning, setWarning] = useState(null);
    const [error, setError] = useState(null);

    // OpenRouter Prompt Engineering Training State
    const [showTraining, setShowTraining] = useState(false);
    const [models, setModels] = useState([]);
    // Arranca vacío a propósito: el modelo se toma del primero que devuelve el
    // backend (el recomendado vigente). Antes había un slug fijo acá que
    // OpenRouter dio de baja, y se enviaba en cada petición provocando un 404.
    const [selectedModel, setSelectedModel] = useState('');
    const [userInstruction, setUserInstruction] = useState('');
    const [optimizing, setOptimizing] = useState(false);
    const [optimizationResult, setOptimizationResult] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [savingRule, setSavingRule] = useState(false);
    const [savingMasterPrompt, setSavingMasterPrompt] = useState(false);
    const [masterPromptSuccess, setMasterPromptSuccess] = useState(false);

    const contactName = conversation?.contact
        ? (conversation.contact.first_name ? `${conversation.contact.first_name} ${conversation.contact.last_name || ''}` : conversation.contact.name || 'Cliente')
        : 'Cliente';

    const handleGenerate = async () => {
        if (!conversationId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await generateAiResponse(conversationId);
            setResponseText(data.response || '');
            setReasoning(data.reasoning || '');
            // La IA pidio consultar el inventario y no llego ningun dato: el
            // texto sugerido promete una consulta que nadie hizo.
            setWarning(data.lookup_failed ? (data.warning || 'No se pudo consultar el inventario.') : null);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al conectar con el servicio de IA.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && conversationId) {
            handleGenerate();
            // Fetch OpenRouter available models
            getOpenRouterModels()
                .then(res => {
                    const list = res.models || [];
                    setModels(list);
                    // El primero es el recomendado por el backend; sólo se fija
                    // si el usuario todavía no eligió otro.
                    setSelectedModel(prev => (prev && list.some(m => m.id === prev)) ? prev : (list[0]?.id || ''));
                })
                .catch(() => {});
        } else {
            setResponseText('');
            setReasoning('');
            setError(null);
            setShowTraining(false);
            setUserInstruction('');
            setOptimizationResult(null);
            setSaveSuccess(false);
        }
    }, [isOpen, conversationId]);

    const handleSend = async () => {
        if (!responseText.trim() || !conversationId) return;
        setSending(true);
        try {
            await sendAiResponse(conversationId, responseText);
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            alert(err.response?.data?.message || 'Error al enviar el mensaje por WhatsApp.');
        } finally {
            setSending(false);
        }
    };

    const handleOptimizePrompt = async (applyDirectly = false) => {
        if (!userInstruction.trim() || !conversationId) return;
        setOptimizing(true);
        setOptimizationResult(null);
        setSaveSuccess(false);
        try {
            const res = await improvePromptWithAi(conversationId, {
                instruction: userInstruction,
                model: selectedModel,
                apply_to_strategy: applyDirectly,
            });

            setOptimizationResult(res);

            // Live update suggested response in textarea
            if (res.response) {
                setResponseText(res.response);
            }

            if (applyDirectly) {
                setSaveSuccess(true);
            }
        } catch (err) {
            alert(err.response?.data?.message || 'Error al optimizar prompt con OpenRouter.');
        } finally {
            setOptimizing(false);
        }
    };

    const handleSaveRuleToStrategy = async () => {
        if (!optimizationResult?.consolidated || !conversationId) return;
        setSavingRule(true);
        try {
            await saveLearnedInstructions(conversationId, optimizationResult.consolidated);
            setSaveSuccess(true);
        } catch (err) {
            alert(err.response?.data?.message || 'Error al guardar la regla en la estrategia.');
        } finally {
            setSavingRule(false);
        }
    };

    const handleApplyMasterPromptSuggestion = async () => {
        const updatedPrompt = optimizationResult?.master_prompt_suggestion?.updated_master_prompt;
        if (!updatedPrompt || !conversationId) return;
        setSavingMasterPrompt(true);
        try {
            await applyMasterPromptSuggestion(conversationId, updatedPrompt);
            setMasterPromptSuccess(true);
        } catch (err) {
            alert(err.response?.data?.message || 'Error al actualizar el Master Prompt.');
        } finally {
            setSavingMasterPrompt(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-800 overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between bg-gradient-to-r from-violet-500/10 via-purple-500/5 to-transparent">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-xl bg-violet-600/10 dark:bg-violet-500/20 text-violet-600 dark:text-violet-400 flex items-center justify-center">
                            <span className="material-symbols-outlined text-[20px]">smart_toy</span>
                        </div>
                        <div>
                            <h3 className="font-bold text-base text-slate-900 dark:text-slate-100">Responder con IA</h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Para: <span className="font-medium text-slate-700 dark:text-slate-300">{contactName}</span></p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={sending}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors p-1"
                    >
                        <span className="material-symbols-outlined text-[20px]">close</span>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4 flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="py-12 flex flex-col items-center justify-center text-center space-y-3">
                            <div className="relative">
                                <div className="w-12 h-12 rounded-full border-4 border-violet-200 dark:border-violet-900 border-t-violet-600 animate-spin" />
                                <span className="material-symbols-outlined text-violet-600 text-xl absolute inset-0 m-auto flex items-center justify-center">auto_awesome</span>
                            </div>
                            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Generando respuesta con IA...</p>
                            <p className="text-xs text-slate-400 max-w-xs">Analizando el historial de conversación y contexto de la estrategia para redactar la mejor sugerencia.</p>
                        </div>
                    ) : error ? (
                        <div className="py-6 space-y-3">
                            <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/40 text-rose-700 dark:text-rose-300 text-xs flex items-start gap-2.5">
                                <span className="material-symbols-outlined text-lg shrink-0">error</span>
                                <p className="leading-relaxed">{error}</p>
                            </div>
                            <div className="flex justify-end">
                                <button
                                    onClick={handleGenerate}
                                    className="px-4 py-2 bg-violet-600 text-white font-medium text-xs rounded-xl hover:bg-violet-700 transition"
                                >
                                    Reintentar
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between text-xs">
                                <span className="font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-violet-500 text-[16px]">edit_note</span>
                                    Respuesta sugerida (puedes editarla):
                                </span>
                                <button
                                    onClick={handleGenerate}
                                    disabled={loading || sending}
                                    className="text-violet-600 dark:text-violet-400 hover:underline flex items-center gap-1 font-medium text-[11px]"
                                >
                                    <span className="material-symbols-outlined text-[14px]">refresh</span>
                                    Volver a generar
                                </button>
                            </div>

                            <textarea
                                value={responseText}
                                onChange={(e) => setResponseText(e.target.value)}
                                rows={4}
                                disabled={sending}
                                className="w-full border border-gray-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 rounded-xl p-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-600 text-slate-800 dark:text-slate-100 placeholder-slate-400 leading-relaxed"
                                placeholder="La respuesta generada aparecerá aquí..."
                            />

                            {/* Section: Entrenar / Mejorar IA con OpenRouter */}
                            <div className="border border-purple-100 dark:border-purple-900/40 rounded-xl bg-purple-50/40 dark:bg-purple-950/20 overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setShowTraining(!showTraining)}
                                    className="w-full px-4 py-3 flex items-center justify-between text-xs font-semibold text-purple-700 dark:text-purple-300 hover:bg-purple-100/50 dark:hover:bg-purple-900/30 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-purple-600 text-base">auto_awesome</span>
                                        <span>Entrenar / Mejorar IA con OpenRouter</span>
                                    </div>
                                    <span className="material-symbols-outlined text-base">
                                        {showTraining ? 'expand_less' : 'expand_more'}
                                    </span>
                                </button>

                                {showTraining && (
                                    <div className="p-4 border-t border-purple-100 dark:border-purple-900/40 space-y-3 text-xs">
                                        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-[11px]">
                                            Escribe una instrucción de corrección en lenguaje natural (ej: <i>"Sé más directo y saluda usando su nombre"</i> o <i>"Ofrece un 10% de descuento si compra hoy"</i>). La IA de OpenRouter sintetizará la regla e integrará el cambio.
                                        </p>

                                        {/* Model Selector */}
                                        <div className="flex items-center gap-2">
                                            <label className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 shrink-0">Modelo IA:</label>
                                            <select
                                                value={selectedModel}
                                                onChange={(e) => setSelectedModel(e.target.value)}
                                                className="flex-1 bg-white dark:bg-slate-900 border border-purple-200 dark:border-purple-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-purple-500"
                                            >
                                                {models.map(m => (
                                                    <option key={m.id} value={m.id} title={m.description}>{m.name} ({m.provider})</option>
                                                ))}
                                                {/* Sin lista se manda vacío y el backend elige el recomendado;
                                                    nombrar un modelo acá es lo que dejaba slugs muertos colgados. */}
                                                {models.length === 0 && (
                                                    <option value="">Modelo recomendado</option>
                                                )}
                                            </select>
                                        </div>

                                        {/* Instruction Input */}
                                        <div className="space-y-1">
                                            <textarea
                                                value={userInstruction}
                                                onChange={(e) => setUserInstruction(e.target.value)}
                                                rows={2}
                                                placeholder="Ej: Trata al cliente de usted y no uses emojis..."
                                                className="w-full p-2.5 bg-white dark:bg-slate-900 border border-purple-200 dark:border-purple-800 rounded-lg text-xs text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                            />
                                        </div>

                                        {/* Action buttons */}
                                        <div className="flex items-center justify-between pt-1">
                                            <button
                                                type="button"
                                                onClick={() => handleOptimizePrompt(false)}
                                                disabled={optimizing || !userInstruction.trim()}
                                                className="flex items-center gap-1.5 px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium text-xs rounded-lg disabled:opacity-50 transition shadow-sm"
                                            >
                                                {optimizing ? (
                                                    <>
                                                        <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                        Optimizando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="material-symbols-outlined text-[15px]">psychology</span>
                                                        Optimizar y Probar en Vivo
                                                    </>
                                                )}
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => handleOptimizePrompt(true)}
                                                disabled={optimizing || !userInstruction.trim()}
                                                className="text-purple-700 dark:text-purple-300 hover:underline text-[11px] font-semibold"
                                            >
                                                Optimizar y Guardar en Estrategia
                                            </button>
                                        </div>

                                        {/* Optimization Result Preview */}
                                        {optimizationResult && (
                                            <div className="mt-3 p-3 bg-white dark:bg-slate-900 rounded-lg border border-purple-200 dark:border-purple-800/80 space-y-3">
                                                <div className="flex items-start gap-2">
                                                    <span className="material-symbols-outlined text-purple-600 text-base shrink-0 mt-0.5">verified</span>
                                                    <div className="text-[11px]">
                                                        <p className="font-bold text-purple-900 dark:text-purple-300">Regla Sintetizada:</p>
                                                        <p className="text-slate-700 dark:text-slate-300 font-medium italic mt-0.5">"{optimizationResult.rule}"</p>
                                                        {optimizationResult.explanation && (
                                                            <p className="text-slate-500 dark:text-slate-400 text-[10px] mt-1">{optimizationResult.explanation}</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Master Prompt Suggestion Card */}
                                                {optimizationResult.master_prompt_suggestion?.has_suggestion && (
                                                    <div className="p-3 bg-amber-50/70 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800/60 space-y-2 text-[11px]">
                                                        <div className="flex items-center gap-1.5 text-amber-900 dark:text-amber-300 font-bold">
                                                            <span className="material-symbols-outlined text-base text-amber-600">lightbulb</span>
                                                            <span>Sugerencia de Cambio en el Master Prompt</span>
                                                        </div>

                                                        {optimizationResult.master_prompt_suggestion.target_text && (
                                                            <div className="space-y-1">
                                                                <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Texto Actual a Reemplazar:</p>
                                                                <div className="p-2 rounded bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-900/50 text-rose-800 dark:text-rose-300 font-mono text-[10px] line-through leading-relaxed">
                                                                    {optimizationResult.master_prompt_suggestion.target_text}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {optimizationResult.master_prompt_suggestion.replacement_text && (
                                                            <div className="space-y-1">
                                                                <p className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Texto Sugerido por IA:</p>
                                                                <div className="p-2 rounded bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-900/50 text-emerald-800 dark:text-emerald-300 font-mono text-[10px] leading-relaxed">
                                                                    {optimizationResult.master_prompt_suggestion.replacement_text}
                                                                </div>
                                                            </div>
                                                        )}

                                                        {optimizationResult.master_prompt_suggestion.reason && (
                                                            <p className="text-[10px] text-slate-600 dark:text-slate-400 italic">
                                                                <b>Motivo:</b> {optimizationResult.master_prompt_suggestion.reason}
                                                            </p>
                                                        )}

                                                        {!masterPromptSuccess ? (
                                                            <div className="pt-1 flex justify-end">
                                                                <button
                                                                    type="button"
                                                                    onClick={handleApplyMasterPromptSuggestion}
                                                                    disabled={savingMasterPrompt}
                                                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white font-bold text-[10px] rounded-lg shadow-sm transition"
                                                                >
                                                                    {savingMasterPrompt ? (
                                                                        <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                                    ) : (
                                                                        <span className="material-symbols-outlined text-[13px]">edit_document</span>
                                                                    )}
                                                                    Aplicar Cambio al Master Prompt
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-800 dark:text-emerald-300 rounded text-[10px] font-bold flex items-center gap-1">
                                                                <span className="material-symbols-outlined text-xs">check</span>
                                                                ¡Master Prompt actualizado exitosamente!
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {!saveSuccess ? (
                                                    <div className="pt-2 border-t border-gray-100 dark:border-slate-800 flex justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={handleSaveRuleToStrategy}
                                                            disabled={savingRule}
                                                            className="flex items-center gap-1 text-[11px] font-bold text-emerald-600 dark:text-emerald-400 hover:underline"
                                                        >
                                                            <span className="material-symbols-outlined text-[14px]">save</span>
                                                            {savingRule ? 'Guardando...' : 'Guardar esta regla para el futuro'}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="p-2 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 rounded text-[11px] font-semibold flex items-center gap-1.5">
                                                        <span className="material-symbols-outlined text-sm">check_circle</span>
                                                        ¡Regla guardada exitosamente en la estrategia!
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {warning && (
                                <div className="flex gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50">
                                    <span className="material-symbols-outlined text-amber-600 text-lg shrink-0">warning</span>
                                    <div className="text-xs text-amber-800 dark:text-amber-200">
                                        <p className="font-semibold">Sin datos del inventario</p>
                                        <p className="mt-0.5">{warning}</p>
                                    </div>
                                </div>
                            )}

                            {reasoning && (
                                <details className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-950 p-2.5 rounded-lg border border-gray-100 dark:border-slate-800">
                                    <summary className="cursor-pointer font-medium hover:text-slate-700 dark:hover:text-slate-200">Ver razonamiento de la IA</summary>
                                    <p className="mt-1.5 whitespace-pre-line text-[11px] opacity-80">{reasoning}</p>
                                </details>
                            )}
                        </>
                    )}
                </div>

                {/* Footer Actions */}
                {!loading && !error && (
                    <div className="px-6 py-4 bg-slate-50 dark:bg-slate-950/60 border-t border-gray-100 dark:border-slate-800 flex items-center justify-end gap-3">
                        <button
                            onClick={onClose}
                            disabled={sending}
                            className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 rounded-xl transition"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSend}
                            disabled={sending || !responseText.trim()}
                            className="flex items-center gap-1.5 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-md shadow-violet-600/20 transition-all"
                        >
                            {sending ? (
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <span className="material-symbols-outlined text-[16px]">send</span>
                            )}
                            Enviar respuesta por WhatsApp
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
